import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import type { ApiCategory, ApiCity, ApiProvider } from '../../core/api/api.types';
import { AuthService } from '../../core/auth/auth.service';
import { ProvidersApiService } from '../../core/api/providers-api.service';
import { ReferenceApiService } from '../../core/api/reference-api.service';

type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
type TrustStatus = ApiProvider['trust_status'];

const DAY_LABELS: Record<DayKey, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

const WEEKDAYS: DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri'];
const WEEKEND:  DayKey[] = ['sat', 'sun'];

@Component({
  selector: 'op-provider-profile-page',
  imports: [RouterModule, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './provider-profile.page.html',
  styleUrl: './provider-profile.page.scss',
})
export class ProviderProfilePage implements OnInit {
  private readonly location = inject(Location);
  private readonly route = inject(ActivatedRoute);
  private readonly auth = inject(AuthService);
  private readonly providersApi = inject(ProvidersApiService);
  private readonly referenceApi = inject(ReferenceApiService);

  readonly providerId = this.route.snapshot.paramMap.get('id') ?? '';
  readonly isSuperAdmin = computed(() => this.auth.user()?.role === 'SUPERADMIN');

  readonly provider = signal<ApiProvider | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);

  private readonly cities = signal<ApiCity[]>([]);
  private readonly categories = signal<ApiCategory[]>([]);

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  async ngOnInit(): Promise<void> {
    try {
      const [providerRes, citiesRes, categoriesRes] = await Promise.all([
        this.providersApi.getById(this.providerId),
        this.referenceApi.cities(),
        this.referenceApi.categories(),
      ]);
      this.provider.set(providerRes);
      this.cities.set(citiesRes.items);
      this.categories.set(categoriesRes.items);
    } catch (err: any) {
      this.error.set(err?.message ?? 'Failed to load provider');
    } finally {
      this.loading.set(false);
    }
  }

  back(): void {
    this.location.back();
  }

  // ── Display helpers ───────────────────────────────────────────────────────
  cityLabels(codes: string[]): string {
    if (!codes.length) return 'All cities';
    const map = Object.fromEntries(this.cities().map(c => [c.code, c.label]));
    return codes.map(c => map[c] ?? c).join(', ');
  }

  categoryNames(ids: string[]): string {
    if (!ids.length) return '—';
    const map = Object.fromEntries(this.categories().map(c => [c.category_id, c.name]));
    return ids.map(id => map[id] ?? id).join(', ');
  }

  languageLabel(code: string): string {
    const map: Record<string, string> = {
      en: 'English', fr: 'French', uk: 'Ukrainian',
      hi: 'Hindi', es: 'Spanish', zh: 'Mandarin',
    };
    return map[code] ?? code.toUpperCase();
  }

  contactMethodLabel(method: string): string {
    const map: Record<string, string> = {
      phone: 'Phone', email: 'Email', text: 'Text / SMS',
      whatsapp: 'WhatsApp', any: 'Any',
    };
    return map[method] ?? method;
  }

  trustLabel(status: TrustStatus): string {
    const map: Record<TrustStatus, string> = {
      trusted: 'Trusted', caution: 'Caution',
      pre_block: 'Pre-Block', blocked: 'Blocked',
    };
    return map[status] ?? status;
  }

  // Working hours helpers
  typicalWeekdayHours(wh: ApiProvider['working_hours']): string {
    if (!wh) return '—';
    const open = WEEKDAYS.filter(d => wh[d]?.open);
    if (!open.length) return 'Closed weekdays';
    const froms = [...new Set(open.map(d => wh[d]?.from).filter(Boolean))];
    const tos   = [...new Set(open.map(d => wh[d]?.to).filter(Boolean))];
    if (froms.length === 1 && tos.length === 1) {
      return `${this.formatTime(froms[0]!)} – ${this.formatTime(tos[0]!)}`;
    }
    return 'Varies';
  }

  weekendHours(wh: ApiProvider['working_hours']): string | null {
    if (!wh) return null;
    const open = WEEKEND.filter(d => wh[d]?.open);
    if (!open.length) return null;
    const froms = [...new Set(open.map(d => wh[d]?.from).filter(Boolean))];
    const tos   = [...new Set(open.map(d => wh[d]?.to).filter(Boolean))];
    const days  = open.map(d => DAY_LABELS[d]).join('/');
    if (froms.length === 1 && tos.length === 1) {
      return `${days}: ${this.formatTime(froms[0]!)} – ${this.formatTime(tos[0]!)}`;
    }
    return `${days}: varies`;
  }

  formatDate(iso: string | null): string {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('en-CA', {
        year: 'numeric', month: 'short', day: 'numeric',
      });
    } catch {
      return '—';
    }
  }

  private formatTime(t: string): string {
    const [hStr, mStr] = t.split(':');
    const h = Number(hStr);
    const m = Number(mStr ?? 0);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return m ? `${hour}:${String(m).padStart(2, '0')} ${period}` : `${hour} ${period}`;
  }
}